import { Box, Button, CircularProgress } from "@mui/material";
import Layout from "@/components/layout";
import { useRoute } from "wouter";
import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { type Project } from "db/schema";
import useSWR, { mutate } from "swr";
import { fetchRoles } from "@/lib/api";
import { supabase } from "../../supabaseClient";
import { useToast } from "@/hooks/use-toast";


const SearchRoles = () => {
  const [match, params] = useRoute("/search-roles/:candidateId")
  const candidateId = params?.candidateId
  const [searchQuery, setSearchQuery] = useState("");
  const { data: roles, error: rolesError, isLoading: rolesLoading } = useSWR('projects', fetchRoles);
  const { toast } = useToast();
  const [clickedRoles, setClickedRoles] = useState<Set<string>>(new Set());
  const [isLoadingState, setIsLoadingState] = useState<Record<string, boolean>>({});



  // Filter roless based on search query


  const filteredRoles = useMemo(
    () =>
      roles?.filter((role) => {
        const isAdded = clickedRoles.has(role.id);


        return (
          !isAdded && role.title.toLowerCase().includes(searchQuery.toLowerCase())&&(role.status.toLowerCase()=== "open"|| role.status.toLowerCase() === "in progress")

        );
      }) || [],
    [roles, searchQuery, clickedRoles]
  );

  const handleAddToCandidate = async (roleId: string, candidateId: string) => {
    if (clickedRoles.has(roleId)) return;

    setIsLoadingState(prev => ({ ...prev, [roleId]: true }));

    try {
      setClickedRoles(prev => new Set(prev).add(roleId));

      const { error } = await supabase
        .from("project_candidate")
        .insert({
          project_id: roleId,
          candidate_id: candidateId,
          data_status: "added",
        })
        .single();

      if (error) {
        setClickedRoles(prev => {
          const newSet = new Set(prev);
          newSet.delete(roleId);
          return newSet;
        });

        if (error.code === "23505") {
          toast({
            title: "Error: Already added to the Role", status: "info", duration: 1000,
            isClosable: true
          });
        } else {
          toast({
            title: "Failed to add", status: "error", duration: 1000,
            isClosable: true
          });
        }
      } else {
        toast({
          title: "Successfully added", status: "success", duration: 1000,
          isClosable: true
        });
        mutate("/api/candidates");
      }
    } catch (err) {
      toast({
        title: "Error occurred", status: "error", duration: 1000,
        isClosable: true
      });
    } finally {
      setIsLoadingState(prev => ({ ...prev, [roleId]: false }));
    }
  };



  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex items-center justify-between ">
          <h2 className="text-3xl font-bold tracking-tight">Search Roles</h2>
          <Box>
            <Button
              variant="contained"
              onClick={() => window.history.back()}
              sx={{
                backgroundColor: "black",
                color: "white",
                "&:hover": {
                  backgroundColor: "#333",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </div>
        {/* search-bar */}
        <div>
          <div className="relative w-full">
            <input
              type="text"
              placeholder="Search Roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
            />
          </div>
        </div>

        {/* role cards*/}


        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 ">
          {filteredRoles?.map((role) => (

            <Card
              key={role.id}
              className="relative cursor-pointer hover:shadow-lg"

            >
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="font-extrabold text-gray-900">{role.title}</h3>
                    <Badge
                      variant={role.status === "active" ? "default" : "secondary"}
                    >
                      {role.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{role.description}</p>
                  <div className="space-y-1 text-sm">
                    {role.vendor && (
                      <div className="text-muted-foreground">
                        Vendor: {role.vendor.name}
                      </div>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        marginTop: "8px",
                        backgroundColor: role.isAdded ? "#999" : "#000",
                        color: "#fff",
                        "&:hover": { backgroundColor: role.isAdded ? "#999" : "#333" },
                      }}
                      onClick={() => handleAddToCandidate(role.id, candidateId!)}
                      disabled={role.isAdded || isLoadingState[role.id]}
                    >
                      {isLoadingState[role.id] ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        role.isAdded ? "Added" : "Add to Role"
                      )}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

      </div>

    </Layout>
  );
}


export default SearchRoles;


